import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import {
  transformConditionsFromBackend,
  transformConditionsToBackend,
} from '@/common/components/ConditionsEditor/helpers'
import { booleanFields } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/ShippingMethods/ShippingAvailabilityCriterionDetailConfiguration/constants'
import { shippingMethodsTabs } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/ShippingMethods/ShippingMethodsConfiguration/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { initCriterion } from './constants'
import {
  transformRequestCreateShippingAvailabilityCriterion,
  transformRequestEditShippingAvailabilityCriterion,
  transformResponseGetShippingAvailabilityCriterion,
} from './transformers'

const useShippingAvailabilityCriterion = ({ id, storeId, setToastInfo, storeCurrencyCode }) => {
  const isDuplicate = id?.includes('duplicate-')
  const isNew = id === 'new'
  const criterionId = isDuplicate ? id.replace('duplicate-', '') : id
  const availabilityCriteriaTabRoute = `${routes.configurationPage.logistics.shippingMethods}?tab=${shippingMethodsTabs.availabilityCriteria}`

  const { t } = useTranslation()
  const navigate = useNavigate()

  const [criterion, setCriterion] = useState(initCriterion)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState([])
  const originalCriterion = useRef(initCriterion)

  const { fetchData: fetchAvailabilityCriterion, loading: fetchingAvailabilityCriterion } = useFetch(
    configurationServices.getShippingAvailabilityCriterion,
    { doInitialRequest: false, transformResponse: transformResponseGetShippingAvailabilityCriterion }
  )

  const { fetchData: createAvailabilityCriterion, loading: creatingAvailabilityCriterion } = useFetch(
    configurationServices.createShippingAvailabilityCriterion,
    { doInitialRequest: false, transformRequest: transformRequestCreateShippingAvailabilityCriterion }
  )

  const { fetchData: editAvailabilityCriterion, loading: editingAvailabilityCriterion } = useFetch(
    configurationServices.editShippingAvailabilityCriterion,
    { doInitialRequest: false, transformRequest: transformRequestEditShippingAvailabilityCriterion }
  )

  const loadCriterion = useCallback(async () => {
    if (isNew) return

    try {
      const data = await fetchAvailabilityCriterion({ storeId, criterionId })
      const loaded = {
        ...data,
        conditions: transformConditionsFromBackend(data?.conditions || [], { booleanFields }),
        shipping_method_ids: data?.shipping_method_ids || [],
      }

      originalCriterion.current = loaded
      setCriterion(loaded)
      if (isDuplicate) setHasChanges(true)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }, [isNew, isDuplicate, fetchAvailabilityCriterion, storeId, criterionId, setToastInfo])

  useEffect(() => {
    loadCriterion()
  }, [loadCriterion])

  const onChangeCriterion = paramsEdited => {
    setCriterion(prev => ({ ...prev, ...paramsEdited }))
    setHasChanges(true)
  }

  const revertChanges = () => {
    if (isNew || isDuplicate) {
      navigate(availabilityCriteriaTabRoute)
      return
    }

    setCriterion(originalCriterion.current)
    setErrors([])
    setHasChanges(false)
  }

  const saveChanges = async () => {
    const payload = {
      ...criterion,
      conditions: transformConditionsToBackend(criterion.conditions || [], { defaultCurrency: storeCurrencyCode }),
    }

    try {
      if (isNew || isDuplicate) {
        await createAvailabilityCriterion({ storeId, criterion: payload })
      } else {
        await editAvailabilityCriterion({ storeId, criterionId, criterion: payload })
      }
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      navigate(availabilityCriteriaTabRoute)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    criterion,
    fetchingAvailabilityCriterion,
    creatingAvailabilityCriterion,
    editingAvailabilityCriterion,
    hasChanges,
    errors,
    setErrors,
    onChangeCriterion,
    revertChanges,
    saveChanges,
    isNew,
    isDuplicate,
  }
}

export default useShippingAvailabilityCriterion
